import { useCallback, useMemo, useState } from "react";
import type { FileCategory } from "../../shared/types";
import type { UseFileCategorySelectionResult } from "./types";

export const useFileCategorySelection = (): UseFileCategorySelectionResult => {
  const [floorPlans, setFloorPlans] = useState<File[]>([]);
  const [electricalDiagrams, setElectricalDiagrams] = useState<File[]>([]);
  const [billOfQuantities, setBillOfQuantities] = useState<File[]>([]);
  const [otherFiles, setOtherFiles] = useState<File[]>([]);

  const selectedFiles = useMemo<Record<FileCategory, File[]>>(
    () => ({
      floorPlans,
      electricalDiagrams,
      billOfQuantities,
      otherFiles,
    }),
    [floorPlans, electricalDiagrams, billOfQuantities, otherFiles],
  );

  const setters = useMemo(
    () => ({
      floorPlans: setFloorPlans,
      electricalDiagrams: setElectricalDiagrams,
      billOfQuantities: setBillOfQuantities,
      otherFiles: setOtherFiles,
    }),
    [],
  );

  const hasRequiredFiles = useMemo(
    () =>
      Boolean(
        floorPlans.length ||
          electricalDiagrams.length ||
          billOfQuantities.length ||
          otherFiles.length,
      ),
    [floorPlans, electricalDiagrams, billOfQuantities, otherFiles],
  );

  const pdfFileCount = useMemo(() => {
    const allFiles = [
      ...floorPlans,
      ...electricalDiagrams,
      ...billOfQuantities,
      ...otherFiles,
    ];
    return allFiles.filter((file) => file.name.toLowerCase().endsWith(".pdf"))
      .length;
  }, [floorPlans, electricalDiagrams, billOfQuantities, otherFiles]);

  const handleRemoveFile = useCallback(
    (category: FileCategory, fileToRemove: File) => {
      const setter = setters[category];
      setter((prev) => prev.filter((file) => file !== fileToRemove));
    },
    [setters],
  );

  const resetFileState = useCallback(() => {
    setFloorPlans([]);
    setElectricalDiagrams([]);
    setBillOfQuantities([]);
    setOtherFiles([]);
  }, []);

  return {
    selectedFiles,
    setters,
    hasRequiredFiles,
    pdfFileCount,
    handleRemoveFile,
    resetFileState,
  };
};
